/**
 * The facts line for a library artwork (list row, card, detail header):
 * stitches, colors, added by/when and the digitizer state.
 *
 * Pure derivation over the library / detail payload. Every item is omitted
 * when its data is absent, and nothing is estimated here: the backend sends
 * a stitch count only when something stored or parsed has one, and says
 * whether it is final. No thread count exists in the data (Jake,
 * 2026-10-22), so embroidery shows stitches, never colors.
 */
import { artworkFileKind, ARTWORK_FILE_KIND } from "@/utils/artworkUploadContract";
import {
  approvalBreakdownLabel,
  digitizerStateLabel,
  shortDateLabel,
} from "./detail/statusAxes";

// Methods whose color count means something (an ALLOW-list: a method added
// later shows no color count until it is named here).
const PRINT_METHOD_SLUGS = ["screen_print", "dtf", "htv"];

/**
 * @param {object} artwork library row or detail header
 * @param {string[]} methodSlugs slugs of the artwork's decoration methods
 * @param {{approval?: boolean}} [options] ``approval: false`` leaves the
 *   artwork-level approval item off (the detail page shows it on its
 *   Approval card instead of repeating it in the header)
 * @returns {{key: string, text: string}[]} in display order
 */
export function artworkFactItems(
  artwork,
  methodSlugs = [],
  { approval = true } = {},
) {
  const a = artwork || {};
  const slugs = Array.isArray(methodSlugs) ? methodSlugs : [];
  const items = [];

  if (slugs.includes("embroidery") && a.stitch_count != null) {
    const n = Number(a.stitch_count);
    if (Number.isFinite(n) && n >= 0) {
      const text = a.stitch_count_final
        ? `${n.toLocaleString()} stitches`
        : `~${n.toLocaleString()} stitches (est.)`;
      items.push({ key: "stitches", text });
    }
  }

  const printsColors = slugs.some((s) => PRINT_METHOD_SLUGS.includes(s));
  const isStitchFile =
    artworkFileKind(a.original_filename || a.filename || "") ===
    ARTWORK_FILE_KIND.STITCH;
  if (printsColors && !isStitchFile && a.color_count != null) {
    const n = Number(a.color_count);
    if (Number.isFinite(n) && n > 0) {
      items.push({ key: "colors", text: `${n} ${n === 1 ? "color" : "colors"}` });
    }
  }

  const when = shortDateLabel(a.added_at);
  if (when) {
    items.push({
      key: "added",
      text: a.added_by ? `Added by ${a.added_by} on ${when}` : `Added ${when}`,
    });
  }

  const digitizer = digitizerStateLabel(a.digitizer);
  if (digitizer) items.push({ key: "digitizer", text: digitizer });

  const approvalText = approval ? approvalBreakdownLabel(a.approval_breakdown) : "";
  if (approvalText) items.push({ key: "approval", text: approvalText });

  return items;
}
